import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { useTwoFactorEnforcement } from '@/composables/useTwoFactorEnforcement'

/** Error thrown by the API layer. Preserves the HTTP status and the machine-readable
 *  error code (e.g. "cert_limit_reached") that the backend includes on 402 responses. */
export class ApiError extends Error {
  status?: number
  code?: string

  constructor(message: string, status?: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

// Create axios instance with default config
export const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true, // Send cookies with requests
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add CSRF token if available (for cookie-based auth)
    const csrfToken = getCsrfToken()
    if (csrfToken && config.headers) {
      config.headers['X-CSRF-TOKEN'] = csrfToken
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Handle 403 Forbidden with two_factor_required error - show global 2FA enforcement dialog
    if (error.response?.status === 403) {
      const data = error.response.data as { error?: string; message?: string; requiresTwoFactorSetup?: boolean }
      if (data?.error === 'two_factor_required' && data?.requiresTwoFactorSetup) {
        // Trigger the global 2FA enforcement dialog
        const { triggerEnforcement } = useTwoFactorEnforcement()
        triggerEnforcement(data.message)
        // Return a rejected promise but don't throw - the dialog handles the UX
        return Promise.reject(new Error('Two-factor authentication is required by your organization.'))
      }
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip token refresh for auth-checking endpoints (they're expected to fail for guests)
      const isAuthCheck = originalRequest.url?.includes('/auth/me')
      if (isAuthCheck) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        // Attempt to refresh the token
        await axios.post('/api/auth/refresh', {}, { withCredentials: true })

        // Retry the original request
        return api(originalRequest)
      } catch {
        // Refresh failed - redirect to login
        // Don't redirect if already on login page or public pages
        const publicPaths = ['/', '/help', '/faq', '/privacy', '/error', '/thank-you', '/processing-error', '/status']
        const isPublicPage = publicPaths.some(path => window.location.pathname === path)
        if (!window.location.pathname.includes('/login') && !isPublicPage) {
          window.location.href = '/login?session=expired'
        }
        return Promise.reject(new ApiError('Your session has expired. Please sign in again.', 401))
      }
    }

    // Handle other errors - preserve HTTP status and machine-readable code
    const errorMessage = extractErrorMessage(error)
    const responseData = error.response?.data as { code?: string } | undefined
    return Promise.reject(new ApiError(errorMessage, error.response?.status, responseData?.code))
  }
)

// Helper to get CSRF token from cookie
function getCsrfToken(): string | null {
  const cookies = document.cookie.split('; ')
  const csrfCookie = cookies.find((cookie) => cookie.startsWith('XSRF-TOKEN='))
  return csrfCookie ? decodeURIComponent(csrfCookie.split('=')[1] ?? '') : null
}

// Helper to extract error message from API response
function extractErrorMessage(error: AxiosError): string {
  if (error.response?.data) {
    const data = error.response.data as {
      error?: { message?: string }
      message?: string
      title?: string
    }

    if (data.error?.message) {
      return data.error.message
    }
    if (data.message) {
      return data.message
    }
    if (data.title) {
      return data.title
    }
  }

  if (error.message) {
    return error.message
  }

  return 'An unexpected error occurred'
}

// Export typed API methods for convenience
export const apiService = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    api.get<T>(url, { params }).then((res) => res.data),

  post: <T>(url: string, data?: unknown) => api.post<T>(url, data).then((res) => res.data),

  put: <T>(url: string, data?: unknown) => api.put<T>(url, data).then((res) => res.data),

  patch: <T>(url: string, data?: unknown) => api.patch<T>(url, data).then((res) => res.data),

  delete: <T>(url: string) => api.delete<T>(url).then((res) => res.data),

  /** Downloads a binary file from the API and triggers a browser save. */
  download: async (url: string, fallbackFilename: string) => {
    const res = await api.get(url, { responseType: 'blob' })
    const disposition = res.headers['content-disposition'] as string | undefined
    let filename = fallbackFilename
    const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
    if (match?.[1]) filename = decodeURIComponent(match[1])
    const blobUrl = window.URL.createObjectURL(res.data as Blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(blobUrl)
  }
}

export default api
